/**
 * The reader's box in the iOS app: TrumanKit, reached through the app's bridge
 * (`callAppBox`). Every reply crosses from the app, so each is decoded here.
 */
import type { HeyDexBoxArgs } from "@heyday/news-api/heydex";
import { BoxError, type BoxEntry, type BoxExecResult, type BoxHost, type BoxStat } from "@heydex/shared/box";

import { callAppBox } from "../appMode";

function record(value: unknown): Record<string, unknown> | null {
  return typeof value === "object" && value !== null && !Array.isArray(value) ? (value as Record<string, unknown>) : null;
}

const malformed = (method: string) => new BoxError("failed", `the app's box answered ${method} with a malformed value`);

const isString = (value: unknown): value is string => typeof value === "string";
const isNumber = (value: unknown): value is number => typeof value === "number" && Number.isFinite(value);
const isInteger = (value: unknown): value is number => Number.isInteger(value);
const isBoolean = (value: unknown): value is boolean => typeof value === "boolean";
const optionalFlag = (value: unknown): boolean | null => (value === undefined ? false : isBoolean(value) ? value : null);

function strings(value: unknown): Record<string, string> | null {
  const fields = record(value);
  return fields && Object.values(fields).every(isString) ? (fields as Record<string, string>) : null;
}

function execResult(value: unknown): BoxExecResult {
  const fields = record(value);
  const env = strings(fields?.["env"]);
  const timedOut = optionalFlag(fields?.["timedOut"]);
  const truncated = optionalFlag(fields?.["truncated"]);
  const lost = optionalFlag(fields?.["lost"]);
  if (
    !fields ||
    !isString(fields["stdout"]) ||
    !isString(fields["stderr"]) ||
    !isInteger(fields["exitCode"]) ||
    !isString(fields["cwd"]) ||
    !env ||
    timedOut === null ||
    truncated === null ||
    lost === null
  ) {
    throw malformed("exec");
  }
  return {
    stdout: fields["stdout"],
    stderr: fields["stderr"],
    exitCode: fields["exitCode"],
    cwd: fields["cwd"],
    env,
    timedOut,
    truncated,
    lost,
  };
}

function stat(value: unknown): BoxStat {
  const fields = record(value);
  if (
    !fields ||
    !isBoolean(fields["isFile"]) ||
    !isBoolean(fields["isDirectory"]) ||
    !isBoolean(fields["isSymbolicLink"]) ||
    !isNumber(fields["size"]) ||
    !isNumber(fields["mode"]) ||
    !isNumber(fields["mtimeMs"])
  ) {
    throw malformed("stat");
  }
  return {
    isFile: fields["isFile"],
    isDirectory: fields["isDirectory"],
    isSymbolicLink: fields["isSymbolicLink"],
    size: fields["size"],
    mode: fields["mode"],
    mtimeMs: fields["mtimeMs"],
  };
}

function entry(value: unknown): BoxEntry {
  const fields = record(value);
  if (
    !fields ||
    !isString(fields["name"]) ||
    !isBoolean(fields["isFile"]) ||
    !isBoolean(fields["isDirectory"]) ||
    !isBoolean(fields["isSymbolicLink"])
  ) {
    throw malformed("listDir");
  }
  return {
    name: fields["name"],
    isFile: fields["isFile"],
    isDirectory: fields["isDirectory"],
    isSymbolicLink: fields["isSymbolicLink"],
  };
}

function toBase64(bytes: Uint8Array): string {
  let text = "";
  for (let at = 0; at < bytes.length; at += 32_768) text += String.fromCharCode(...bytes.subarray(at, at + 32_768));
  return btoa(text);
}

function fromBase64(value: unknown): Uint8Array {
  if (!isString(value)) throw malformed("readFile");
  let text: string;
  try {
    text = atob(value);
  } catch {
    throw malformed("readFile");
  }
  return Uint8Array.from(text, (character) => character.charCodeAt(0));
}

export function appBox(): BoxHost {
  return {
    exec: async (command, options = {}) => {
      const sent: HeyDexBoxArgs["exec"][1] = {
        ...(options.cwd !== undefined ? { cwd: options.cwd } : {}),
        ...(options.env !== undefined ? { env: { ...options.env } } : {}),
        ...(options.stdin !== undefined ? { stdin: options.stdin } : {}),
        ...(options.deadlineMs !== undefined ? { deadlineMs: options.deadlineMs } : {}),
      };
      return execResult(await callAppBox("exec", [command, sent]));
    },
    interrupt: async () => {
      await callAppBox("interrupt", []);
    },
    readFile: async (path) => fromBase64(await callAppBox("readFile", [path])),
    writeFile: async (path, content) => {
      await callAppBox("writeFile", [path, toBase64(content)]);
    },
    stat: async (path) => stat(await callAppBox("stat", [path])),
    listDir: async (path) => {
      const entries = await callAppBox("listDir", [path]);
      if (!Array.isArray(entries)) throw malformed("listDir");
      return entries.map(entry);
    },
    mkdir: async (path, { recursive }) => {
      await callAppBox("mkdir", [path, { recursive }]);
    },
    remove: async (path, { recursive, force }) => {
      await callAppBox("remove", [path, { recursive, force }]);
    },
    move: async (from, to) => {
      await callAppBox("move", [from, to]);
    },
  };
}
